/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Subject } from 'rxjs';
import { WorkBook, Sheet, writeFile } from 'xlsx';

export interface ISpreadsheetException {
	type: SpreadsheetExceptionType;
	message?: string;
	data?: object;
}

export interface ISpreadsheetValidation {
	pass: boolean;
	message?: string;
}

export enum SpreadsheetInputType {
	Base64 = 'base64',
	Binary = 'binary',
	Buffer = 'buffer',
	File = 'file',
	Array = 'array',
	String = 'string',
}

// data integrity issues, requires user manual correction
export enum SpreadsheetExceptionType {
	MissingId, // can't find the external schedule id in spreadsheet
	DuplicateId, // tickets have same external schedule ids
}

/**
 * This base process the spreadsheet
 * The workflow is largely: validate → prepare → process → (complete event) → export (download) → destroy
 */
export abstract class SpreadsheetBase {
	public exception$: Subject<ISpreadsheetException>;
	public processComplete$: Subject<number>; // num matches

	protected destroy$: Subject<void>;
	protected workbook: WorkBook;
	protected sheet: Sheet; // string[][]

	constructor() {
		this.exception$ = new Subject<ISpreadsheetException>();
		this.processComplete$ = new Subject<number>();
		this.destroy$ = new Subject<void>();
	}

	/**
	 * Validate spreadsheet, child class needs to implement validation
	 * @param file spreadsheet file, should be in binary format, e.g. binaryString from file-upload.component
	 * @param inputType use SpreadsheetInputType.Binary in conjunction with file-upload.component's binaryString
	 */
	public abstract validate(file: any, inputType: SpreadsheetInputType): ISpreadsheetValidation;

	/**
	 * Any preparation work before processing data
	 */
	public abstract prepare(): void;

	/**
	 * Process spreadsheet
	 */
	public abstract process<T>(data: object): void;

	/**
	 * Process completed, post process data
	 * Use this to notify caller of completion
	 */
	public complete(): void {
		this.processComplete$.next(undefined);
	}

	/**
	 * Export and download the updated spreadsheet
	 * @param filename filename to download
	 */
	public export(filename: string): void {
		writeFile(this.workbook, filename);
	}

	public destroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.exception$.complete();
		this.processComplete$.complete();
		this.destroy$ = null;
		this.exception$ = null;
		this.processComplete$ = null;
		this.workbook = null;
		this.sheet = null;
	}

	protected abstract parse(file: any, inputType: SpreadsheetInputType): ISpreadsheetValidation;
}
